import React from 'react';
import { useGetGlobalStorefrontSettingsQuery, useGetStorefrontQuery, usePublishStorefrontMutation, useResetStorefrontCacheSeedMutation, useRevertStorefrontMutation, useLazyGetStorefrontLoginLinkQuery } from '../../store/endpoints/StorefrontSettingsEndpoints';
import { CommandBar, DefaultButton, Dialog, DialogFooter, DialogType, ICommandBarItemProps, IContextualMenuProps, PrimaryButton, Spinner, SpinnerSize } from '@fluentui/react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import { useGetOrganizationEntitlementsQuery } from '../../store/endpoints/OrganizationsEndpoints';
import { useGetStorefrontTemplatesQuery } from '../../store/endpoints/StorefrontTemplateEndpoints';
import useCan, { STOREFRONT_TEMPLATES } from '../../permissions/ability';
import SaveStorefrontTemplateModal from './Templates/SaveStorefrontTemplateModal';
import EditStorefrontTemplateModal from './Templates/EditStorefrontTemplateModal';
import StorefrontTemplatePickerModal from './Templates/StorefrontTemplatePickerModal';
import ApplyTemplateWizard from './Templates/ApplyTemplateWizard';
import useStorefrontEditLease from '../../hooks/useStorefrontEditLease';
import { StorefrontEditLockNotice } from './StorefrontEditLock';
import { storefrontSaveErrorMessage } from '../../logic/storefrontLeaseConflict';
import { SESSION_ERROR, sessionErrorFrom } from './storefrontSessionError';

interface IStorefrontSettingsTitleBarProps {
    storefront: string;
    title: string;
    additionalActions?: ICommandBarItemProps[];
    onSettingsChanged?: () => void;
}


const StorefrontSettingsTitleBar: React.FC<IStorefrontSettingsTitleBarProps> = (props) => {
    const globalSettings = useGetGlobalStorefrontSettingsQuery();
    const storefrontQuery = useGetStorefrontQuery(props.storefront, { skip: !props.storefront });
    // Which link action is in flight, not just whether one is. Minting a storefront session takes a
    // few seconds — long enough that a button which only greys out reads as broken — so the one that
    // was clicked says so while every other stays disabled.
    const [gettingLink, setGettingLink] = React.useState<string | undefined>();
    const [getStorefrontLink] = useLazyGetStorefrontLoginLinkQuery();
    const [goLive] = usePublishStorefrontMutation();
    const [revert] = useRevertStorefrontMutation();
    const [resetCacheSeed] = useResetStorefrontCacheSeedMutation();
    const [clearingCache, setClearingCache] = React.useState(false);
    const [confirmPublish, setConfirmPublish] = React.useState(false);
    const [confirmRevert, setConfirmRevert] = React.useState(false);
    const org = useSelector((state: RootState) => state.settings.selectedOrganization);
    const getOrgEntitlements = useGetOrganizationEntitlementsQuery(org ?? '', { skip: !org || org === '' });
    const canTemplates = useCan(STOREFRONT_TEMPLATES);
    const templates = useGetStorefrontTemplatesQuery(undefined, { skip: !canTemplates });
    const [saveTemplateOpen, setSaveTemplateOpen] = React.useState(false);
    const [editTemplateOpen, setEditTemplateOpen] = React.useState(false);
    // Applying a template is two screens: the gallery picks one, then the wizard decides what of it to
    // take. Kept separate so the gallery closes as the wizard opens rather than stacking.
    const [applyPickerOpen, setApplyPickerOpen] = React.useState(false);
    const [applyTemplateId, setApplyTemplateId] = React.useState<{id: string, name?: string}>();
    // This bar is on every storefront-scoped screen, so it is the one place the "the assistant is
    // working on this storefront" notice has to be written. It also owns Publish and Revert, which the
    // API guards with the same lease, so both are switched off here while it is held rather than left
    // to fail with a 409.
    const editLease = useStorefrontEditLease(props.storefront);

    // The template (if any) captured from this storefront — most recently updated wins. Only the
    // caller's own-org templates carry sourceStorefrontId, so this never matches a global template
    // from another organization.
    const ownTemplate = React.useMemo(() => {
        const matches = (templates.data?.items ?? []).filter(t => t.sourceStorefrontId === props.storefront);
        if (matches.length === 0) return undefined;
        return [...matches].sort((a, b) => (b.lastUpdated ?? '').localeCompare(a.lastUpdated ?? ''))[0];
    }, [templates.data, props.storefront]);

    const loginPageUrl = () => {
        const storefront = storefrontQuery.data;
        const domains = getOrgEntitlements.data?.storefrontDomains;
        const subUrl = storefront?.liveSubUrl ?? storefront?.subUrl;
        if (!storefront || !domains || !subUrl) return undefined;
        if (storefront.domain && domains.includes(storefront.domain)) {
            return `https://${storefront.domain}/${org}/${subUrl}/login`;
        }
        if ((storefront.domain == null || storefront.domain === '') && domains.length > 0) {
            return `https://${domains[0]}/${org}/${subUrl}/login`;
        }
        return undefined;
    };

    const previewStore = (usePreview: boolean) => {
        if (storefrontQuery.data?.subUrl) {
            const subUrl = usePreview
                ? storefrontQuery.data.previewSubUrl ?? `${storefrontQuery.data.subUrl}-preview`
                : storefrontQuery.data.liveSubUrl ?? storefrontQuery.data.subUrl;
            setGettingLink(usePreview ? 'preview-draft' : 'preview-live');
            getStorefrontLink(subUrl).unwrap().then((resp) => {
                if (resp.redirectUrl) {
                    window.open(resp.redirectUrl, '_blank');
                } else {
                    toast.error(SESSION_ERROR);
                }
            }).catch((error) => {
                toast.error(sessionErrorFrom(error));
            }).finally(() => setGettingLink(undefined));
        }
    };

    const copyPreviewUrl = (usePreview: boolean, label: string) => {
        if (!storefrontQuery.data?.subUrl) return;
        const subUrl = usePreview
            ? storefrontQuery.data.previewSubUrl ?? `${storefrontQuery.data.subUrl}-preview`
            : storefrontQuery.data.liveSubUrl ?? storefrontQuery.data.subUrl;
        setGettingLink(usePreview ? 'copy-draft' : 'copy-live');
        const linkPromise = getStorefrontLink(subUrl).unwrap().then((resp) => {
            if (!resp.redirectUrl) throw new Error('No preview URL returned');
            return resp.redirectUrl;
        });
        // Safari only permits clipboard writes issued synchronously from the user
        // gesture, so pass a promise-backed ClipboardItem instead of awaiting first.
        const copy = typeof ClipboardItem !== 'undefined' && navigator.clipboard.write
            ? navigator.clipboard.write([new ClipboardItem({
                'text/plain': linkPromise.then((url) => new Blob([url], { type: 'text/plain' })),
            })])
            : linkPromise.then((url) => navigator.clipboard.writeText(url));
        copy.then(() => {
            toast.success(`${label} URL copied to clipboard`);
        }).catch(() => {
            toast.error(`Failed to copy ${label} URL`);
        }).finally(() => setGettingLink(undefined));
    };

    const handleGoLive = async () => {
        if (!props.storefront) return;
        setConfirmPublish(false);
        const toastId = toast.loading('Publishing...');
        try {
            setGettingLink('publish');
            await goLive(props.storefront).unwrap();
            toast.update(toastId, { type: 'success', render: 'Published', isLoading: false, autoClose: 3000 });
            props.onSettingsChanged?.();
        } catch (e) {
            // A publish can still lose the race: the assistant may take the lease between the last poll
            // and this request. Say so plainly instead of reporting a generic failure.
            toast.update(toastId, {
                type: 'error',
                render: storefrontSaveErrorMessage(e, 'Failed to publish storefront'),
                isLoading: false,
                autoClose: 4000,
            });
        } finally {
            setGettingLink(undefined);
        }
    };

    const handleRevert = async () => {
        if (!props.storefront) return;
        setConfirmRevert(false);
        const toastId = toast.loading('Reverting...');
        try {
            await revert(props.storefront).unwrap();
            toast.update(toastId, { type: 'success', render: 'Reverted', isLoading: false, autoClose: 3000 });
            props.onSettingsChanged?.();
        } catch (e) {
            toast.update(toastId, {
                type: 'error',
                render: storefrontSaveErrorMessage(e, 'Failed to revert storefront'),
                isLoading: false,
                autoClose: 4000,
            });
        }
    };

    const handleClearCache = async () => {
        const subUrl = storefrontQuery.data?.subUrl;
        if (!subUrl) return;
        try {
            setClearingCache(true);
            await resetCacheSeed({ subUrl }).unwrap();
            toast.success('Cache cleared successfully');
        } catch {
            toast.error('Failed to clear cache');
        } finally {
            setClearingCache(false);
        }
    };

    const hasPreview = storefrontQuery.data?.hasPreview;

    const actions: ICommandBarItemProps[] = [
        {
            key: 'PreviewLive',
            text: gettingLink === 'preview-live'
                ? 'Starting session...'
                : (globalSettings.data?.usePreview ? 'Preview (Live)' : 'Preview'),
            onClick: () => previewStore(false),
            disabled: !storefrontQuery.data?.subUrl || !!gettingLink,
            iconProps: { iconName: 'View' },
            onRenderIcon: gettingLink === 'preview-live'
                ? () => <Spinner size={SpinnerSize.small} />
                : undefined,
        },
        ...(globalSettings.data?.usePreview ? [
            {
                key: 'PreviewDraft',
                text: gettingLink === 'preview-draft' ? 'Starting session...' : 'Preview (Draft)',
                onClick: () => previewStore(true),
                disabled: !storefrontQuery.data?.subUrl || !hasPreview || !!gettingLink,
                iconProps: { iconName: 'View' },
                onRenderIcon: gettingLink === 'preview-draft'
                    ? () => <Spinner size={SpinnerSize.small} />
                    : undefined,
            },
            {
                key: 'GoLive',
                text: 'Publish',
                onClick: () => setConfirmPublish(true),
                disabled: !hasPreview || editLease.locked,
                title: editLease.locked ? 'Paused while changes are being applied to this storefront' : undefined,
                iconProps: { iconName: 'Deploy' },
            },
        ] as ICommandBarItemProps[] : []),
        ...(props.additionalActions || []),
        {
            key: 'MoreOptions',
            text: '',
            subMenuProps: {
                items: [
                    {
                        key: 'LoginPage',
                        text: 'Login Page',
                        iconProps: { iconName: 'Signin' },
                        onClick: () => { window.open(loginPageUrl(), '_blank', 'noopener,noreferrer'); },
                        disabled: !loginPageUrl(),
                    },
                    {
                        key: 'CopyPreviewLiveUrl',
                        text: gettingLink === 'copy-live'
                            ? 'Starting session...'
                            : (globalSettings.data?.usePreview ? 'Copy Preview (Live) URL' : 'Copy Preview URL'),
                        iconProps: { iconName: 'Copy' },
                        onClick: () => { copyPreviewUrl(false, globalSettings.data?.usePreview ? 'Preview (Live)' : 'Preview'); },
                        disabled: !storefrontQuery.data?.subUrl || !!gettingLink,
                    },
                    ...(globalSettings.data?.usePreview ? [
                        {
                            key: 'CopyPreviewDraftUrl',
                            text: gettingLink === 'copy-draft' ? 'Starting session...' : 'Copy Preview (Draft) URL',
                            iconProps: { iconName: 'Copy' },
                            onClick: () => { copyPreviewUrl(true, 'Preview (Draft)'); },
                            disabled: !storefrontQuery.data?.subUrl || !hasPreview || !!gettingLink,
                        },
                    ] : []),
                    {
                        key: 'Revert',
                        text: 'Revert',
                        onClick: () => setConfirmRevert(true),
                        disabled: !hasPreview || editLease.locked,
                        title: editLease.locked ? 'Paused while changes are being applied to this storefront' : undefined,
                        iconProps: { iconName: 'Undo' },
                    },
                    {
                        key: 'ClearCache',
                        text: clearingCache ? 'Clearing...' : 'Clear Cache',
                        iconProps: { iconName: 'Refresh' },
                        onClick: handleClearCache,
                        disabled: !storefrontQuery.data?.subUrl || clearingCache,
                    },
                    ...(canTemplates ? [
                        ownTemplate
                            ? {
                                key: 'TemplateSettings',
                                text: 'Template Settings',
                                iconProps: { iconName: 'FileTemplate' },
                                onClick: () => setEditTemplateOpen(true),
                            }
                            : {
                                key: 'SaveAsTemplate',
                                text: 'Save as Template',
                                iconProps: { iconName: 'FileTemplate' },
                                onClick: () => setSaveTemplateOpen(true),
                                disabled: !storefrontQuery.data,
                            },
                    ] : []),
                    // Only offered when the organization stages storefront changes through a preview:
                    // applying a template writes to the draft, and Revert is the whole undo story, so without
                    // a preview there would be no way back from a template that turns out to be wrong. The API
                    // refuses it in that case too - hiding a menu item is not a guard. Outside the
                    // canTemplates gate above deliberately: that one is about AUTHORING templates, and using
                    // one is an ordinary storefront-admin action.
                    ...(globalSettings.data?.usePreview ? [{
                        key: 'ApplyTemplate',
                        text: 'Apply a Template',
                        iconProps: { iconName: 'Color' },
                        disabled: !storefrontQuery.data || editLease.locked,
                        title: editLease.locked
                            ? 'Paused while changes are being applied to this storefront'
                            : undefined,
                        onClick: () => setApplyPickerOpen(true),
                    }] : []),
                ],
            } as unknown as IContextualMenuProps,
        },
    ];

    return (
        <div>
            <CommandBar
                items={[
                    {
                        key: 'title',
                        onRender: () => <h1 style={{margin: 0}}>{props.title}</h1>
                    }
                ]}
                farItems={actions}
                />
            <StorefrontEditLockNotice locked={editLease.locked} heldByAssistant={editLease.heldByAssistant} />
            <Dialog
                hidden={!confirmPublish}
                onDismiss={() => setConfirmPublish(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Publish Storefront',
                    subText: 'Are you sure you want to publish? This will make your draft settings live.',
                }}
            >
                <DialogFooter>
                    <PrimaryButton text="Publish" onClick={handleGoLive} />
                    <DefaultButton text="Cancel" onClick={() => setConfirmPublish(false)} />
                </DialogFooter>
            </Dialog>
            <Dialog
                hidden={!confirmRevert}
                onDismiss={() => setConfirmRevert(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Revert Storefront',
                    subText: 'Are you sure you want to revert? This will discard your draft and restore the live settings.',
                }}
            >
                <DialogFooter>
                    <PrimaryButton text="Revert" onClick={handleRevert} />
                    <DefaultButton text="Cancel" onClick={() => setConfirmRevert(false)} />
                </DialogFooter>
            </Dialog>
            {saveTemplateOpen && storefrontQuery.data && (
                <SaveStorefrontTemplateModal
                    isOpen={saveTemplateOpen}
                    onDismiss={() => setSaveTemplateOpen(false)}
                    storefront={{
                        id: props.storefront,
                        name: storefrontQuery.data.name ?? storefrontQuery.data.subUrl ?? '',
                    }}
                />
            )}
            {applyPickerOpen && (
                <StorefrontTemplatePickerModal
                    isOpen={applyPickerOpen}
                    onDismiss={() => setApplyPickerOpen(false)}
                    selectLabel='Use this design'
                    onSelectTemplate={(template) => {
                        // The gallery's "blank storefront" card has no template; there is nothing to apply.
                        if (!template?.id) return;
                        setApplyPickerOpen(false);
                        setApplyTemplateId({ id: template.id, name: template.name });
                    }}
                    onEditTemplate={() => { /* editing is reached from Template Settings, not from here */ }}
                />
            )}
            {applyTemplateId && (
                <ApplyTemplateWizard
                    isOpen
                    onDismiss={() => setApplyTemplateId(undefined)}
                    storefrontId={props.storefront}
                    templateId={applyTemplateId.id}
                    templateName={applyTemplateId.name}
                    onApplied={() => {
                        toast.success(
                            "Applied to this storefront's draft. Preview it, then publish to make it "
                            + 'live, or revert to discard it.');
                        // Same contract publish and revert use: the server's copy has moved, so whatever this
                        // page is rendering from has to be re-read. Without it an apply lands correctly and
                        // the screen keeps showing what was there before, which reads as nothing happening.
                        props.onSettingsChanged?.();
                    }}
                />
            )}
            {editTemplateOpen && ownTemplate?.id && (
                <EditStorefrontTemplateModal
                    isOpen={editTemplateOpen}
                    onDismiss={() => setEditTemplateOpen(false)}
                    templateId={ownTemplate.id}
                    onDeleted={() => setEditTemplateOpen(false)}
                />
            )}
        </div>
    );
};

export default StorefrontSettingsTitleBar;
